export type SettingsNavigationScope = 'personal' | 'workspace' | 'archived';

export interface SettingsNavigationState {
  scope: SettingsNavigationScope;
  slug: string;
}

export interface SettingsNavigationPermissions {
  canManageBilling: boolean;
  canViewStorageLimits: boolean;
  canViewBilling: boolean;
  showFilePermissions: boolean;
}

export interface SettingsNavigationItem {
  href: string;
  icon:
    | 'archive'
    | 'analytics'
    | 'appearance'
    | 'billing'
    | 'file-permissions'
    | 'invitations'
    | 'members'
    | 'memories'
    | 'pricing'
    | 'summarization'
    | 'profile'
    | 'roles'
    | 'security'
    | 'storage'
    | 'teams'
    | 'usage'
    | 'workspace'
    | 'workspaces';
  label:
    | 'archivedChats'
    | 'analytics'
    | 'appearance'
    | 'billing'
    | 'filePermissions'
    | 'invitations'
    | 'members'
    | 'memories'
    | 'summarization'
    | 'pricing'
    | 'profile'
    | 'roles'
    | 'security'
    | 'storage'
    | 'teams'
    | 'usage'
    | 'workspace'
    | 'workspaces';
  searchTerms: readonly string[];
  slug: string;
}

export function getSettingsNavigationState(
  pathname: string | null
): SettingsNavigationState {
  const parts = pathname?.split('/').filter(Boolean) ?? [];
  if (parts[0] === 'settings') {
    return { scope: 'personal', slug: parts[1] ?? 'profile' };
  }

  const settingsIndex = parts.indexOf('settings');
  return {
    scope:
      parts[settingsIndex + 1] === 'archived-chats' ? 'archived' : 'workspace',
    slug: settingsIndex >= 0 ? (parts[settingsIndex + 1] ?? 'workspace') : '',
  };
}

export function getPersonalSettingsNavigationItems(): SettingsNavigationItem[] {
  return [
    {
      href: '/settings/profile',
      label: 'profile',
      slug: 'profile',
      icon: 'profile',
      searchTerms: ['account', 'name', 'email', 'username', 'avatar'],
    },
    {
      href: '/settings/memories',
      label: 'memories',
      slug: 'memories',
      icon: 'memories',
      searchTerms: ['memory', 'personalization', 'remembered information'],
    },
    {
      href: '/settings/summarization',
      label: 'summarization',
      slug: 'summarization',
      icon: 'summarization',
      searchTerms: [
        'summary',
        'summarize',
        'voice',
        'speech',
        'speed',
        'talk back',
      ],
    },
    {
      href: '/settings/appearance',
      label: 'appearance',
      slug: 'appearance',
      icon: 'appearance',
      searchTerms: [
        'theme',
        'dark mode',
        'light mode',
        'language',
        'haptics',
        'default workspace',
      ],
    },
    {
      href: '/settings/security',
      label: 'security',
      slug: 'security',
      icon: 'security',
      searchTerms: ['password', 'authentication', 'login', 'account access'],
    },
    {
      href: '/settings/organizations',
      label: 'workspaces',
      slug: 'organizations',
      icon: 'workspaces',
      searchTerms: ['organizations', 'switch workspace', 'default workspace'],
    },
    {
      href: '/settings/invitations',
      label: 'invitations',
      slug: 'invitations',
      icon: 'invitations',
      searchTerms: ['invites', 'pending invitations', 'join workspace'],
    },
  ];
}

export function getWorkspaceSettingsNavigationItems(args: {
  orgSlug: string | null;
  permissions: SettingsNavigationPermissions;
}): SettingsNavigationItem[] {
  if (!args.orgSlug) return [];
  const prefix = `/${args.orgSlug}/settings`;
  const items: Array<SettingsNavigationItem & { visible?: boolean }> = [
    {
      href: `${prefix}/workspace`,
      label: 'workspace',
      slug: 'workspace',
      icon: 'workspace',
      searchTerms: [
        'organization',
        'name',
        'logo',
        'slug',
        'workspace details',
      ],
    },
    {
      href: `${prefix}/members`,
      label: 'members',
      slug: 'members',
      icon: 'members',
      searchTerms: ['people', 'team members', 'invite members', 'access'],
    },
    {
      href: `${prefix}/teams`,
      label: 'teams',
      slug: 'teams',
      icon: 'teams',
      searchTerms: ['groups', 'member groups', 'team access'],
    },
    {
      href: `${prefix}/roles`,
      label: 'roles',
      slug: 'roles',
      icon: 'roles',
      searchTerms: ['permissions', 'access control', 'member role'],
    },
    {
      href: `${prefix}/analytics`,
      label: 'analytics',
      slug: 'analytics',
      icon: 'analytics',
      searchTerms: ['activity', 'insights', 'metrics', 'reports'],
    },
    {
      href: `${prefix}/memories`,
      label: 'memories',
      slug: 'memories',
      icon: 'memories',
      searchTerms: ['memory', 'workspace context', 'shared knowledge'],
    },
    {
      href: `${prefix}/file-permissions`,
      label: 'filePermissions',
      slug: 'file-permissions',
      icon: 'file-permissions',
      searchTerms: [
        'files',
        'folders',
        'workspace files',
        'access control',
        'filesystem permissions',
      ],
      visible: args.permissions.showFilePermissions,
    },
    {
      href: `${prefix}/storage`,
      label: 'storage',
      slug: 'storage',
      icon: 'storage',
      searchTerms: [
        'storage',
        'files used',
        'storage limit',
        'team storage',
        'personal storage',
      ],
      visible: args.permissions.canViewStorageLimits,
    },
    {
      href: `${prefix}/purchase`,
      label: 'pricing',
      slug: 'purchase',
      icon: 'pricing',
      searchTerms: ['plans', 'subscription', 'credits', 'top up', 'upgrade'],
      visible: args.permissions.canManageBilling,
    },
    {
      href: `${prefix}/usage`,
      label: 'usage',
      slug: 'usage',
      icon: 'usage',
      searchTerms: ['credits used', 'consumption', 'usage history', 'spending'],
      visible: args.permissions.canViewBilling,
    },
    {
      href: `${prefix}/billing`,
      label: 'billing',
      slug: 'billing',
      icon: 'billing',
      searchTerms: ['invoice', 'payment', 'receipt', 'subscription billing'],
      visible: args.permissions.canViewBilling,
    },
  ];

  return items.filter((item) => item.visible !== false);
}

export function getArchivedSettingsNavigationItems(
  orgSlug: string | null
): SettingsNavigationItem[] {
  return orgSlug
    ? [
        {
          href: `/${orgSlug}/settings/archived-chats`,
          icon: 'archive',
          label: 'archivedChats',
          slug: 'archived-chats',
          searchTerms: ['archive', 'restore', 'chats'],
        },
      ]
    : [];
}
